// Pure barcode format detection. Browser- and SSR-safe.
import { computeCheckDigit, expandUpcE } from "./gtin";

export type FormatKey =
  | "UPC-A"
  | "UPC-E"
  | "EAN-8"
  | "EAN-13"
  | "GTIN-14"
  | "ISBN-10"
  | "ISBN-13"
  | "QR / Text"
  | "Unknown";

export type Confidence = "high" | "medium" | "low";

export type Candidate = {
  format: FormatKey;
  confidence: Confidence;
  reason: string;
  /** Tool path that best handles this format, if any. */
  nextTool?: { path: string; label: string };
};

export type DetectionResult = {
  state: "idle" | "empty" | "ok";
  cleaned: string;
  candidates: Candidate[];
};

function isbn10Check(body9: string): string {
  // mod-11 weighted 10..2
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(body9[i]) * (10 - i);
  const r = (11 - (sum % 11)) % 11;
  return r === 10 ? "X" : String(r);
}

function validIsbn10(raw10: string): boolean {
  if (!/^\d{9}[\dX]$/.test(raw10)) return false;
  return isbn10Check(raw10.slice(0, 9)) === raw10[9].toUpperCase();
}

function gtinValid(raw: string): boolean {
  return computeCheckDigit(raw.slice(0, -1)) === Number(raw[raw.length - 1]);
}

const TOOLS: Partial<Record<FormatKey, { path: string; label: string }>> = {
  "UPC-A": { path: "/upc-lookup", label: "UPC Lookup" },
  "UPC-E": { path: "/upc-lookup", label: "UPC Lookup" },
  "EAN-8": { path: "/ean-lookup", label: "EAN Lookup" },
  "EAN-13": { path: "/ean-lookup", label: "EAN Lookup" },
  "GTIN-14": { path: "/gtin-validator", label: "GTIN Validator" },
  "ISBN-10": { path: "/isbn-barcode-reader", label: "ISBN Barcode Reader" },
  "ISBN-13": { path: "/isbn-barcode-reader", label: "ISBN Barcode Reader" },
  "QR / Text": { path: "/qr-code-reader", label: "QR Code Reader" },
};

function withTool(c: Omit<Candidate, "nextTool">): Candidate {
  const t = TOOLS[c.format];
  return t ? { ...c, nextTool: t } : c;
}

export function detectBarcode(input: string): DetectionResult {
  // Strip spaces, hyphens, dots and underscores — the rest is preserved.
  const cleaned = input.replace(/[\s\-_.]/g, "");
  if (input.trim() === "") return { state: "idle", cleaned: "", candidates: [] };
  if (cleaned === "") return { state: "empty", cleaned, candidates: [] };

  const candidates: Candidate[] = [];
  const len = cleaned.length;
  const isAllDigits = /^\d+$/.test(cleaned);

  if (isAllDigits) {
    // GTIN-8
    if (len === 8) {
      const ok = gtinValid(cleaned);
      candidates.push(
        withTool({
          format: "EAN-8",
          confidence: ok ? "high" : "low",
          reason: ok
            ? "8 digits and the GS1 mod-10 check digit matches — this is a valid EAN-8 / GTIN-8."
            : "8 digits but the GS1 mod-10 check digit doesn't match. Could be a typo.",
        }),
      );
    }

    // UPC-E (6, 7 or 8 digits, but 8 collides with EAN-8 above)
    if (len === 6 || len === 7 || len === 8) {
      const expanded = expandUpcE(cleaned);
      if (expanded) {
        candidates.push(
          withTool({
            format: "UPC-E",
            confidence: len === 8 ? "high" : "medium",
            reason:
              len === 8
                ? `8 digits that expand cleanly to UPC-A ${expanded}. Likely UPC-E.`
                : `${len} digits that expand to UPC-A ${expanded}. UPC-E payloads are commonly 6–7 digits without the check digit.`,
          }),
        );
      }
    }

    // UPC-A / GTIN-12
    if (len === 12) {
      const ok = gtinValid(cleaned);
      candidates.push(
        withTool({
          format: "UPC-A",
          confidence: ok ? "high" : "low",
          reason: ok
            ? "12 digits and the GS1 mod-10 check digit matches — this is a valid UPC-A / GTIN-12."
            : "12 digits but the GS1 mod-10 check digit doesn't match. Could be a typo or a non-GTIN 12-digit code.",
        }),
      );
    }

    // EAN-13 / GTIN-13 / ISBN-13
    if (len === 13) {
      const ok = gtinValid(cleaned);
      const prefix = cleaned.slice(0, 3);
      const looksIsbn = prefix === "978" || prefix === "979";
      if (looksIsbn) {
        candidates.push(
          withTool({
            format: "ISBN-13",
            confidence: ok ? "high" : "low",
            reason: ok
              ? `Starts with ${prefix} (Bookland) and the GS1 mod-10 check digit matches — valid ISBN-13.`
              : `Starts with ${prefix} (Bookland) but the check digit doesn't match. Likely a mistyped ISBN-13.`,
          }),
        );
      }
      candidates.push(
        withTool({
          format: "EAN-13",
          confidence: ok ? (looksIsbn ? "medium" : "high") : "low",
          reason: ok
            ? looksIsbn
              ? "13 digits with a valid check digit. Any ISBN-13 is also a valid EAN-13."
              : "13 digits and the GS1 mod-10 check digit matches — valid EAN-13 / GTIN-13."
            : "13 digits but the GS1 mod-10 check digit doesn't match.",
        }),
      );
    }

    // GTIN-14 / ITF-14
    if (len === 14) {
      const ok = gtinValid(cleaned);
      candidates.push(
        withTool({
          format: "GTIN-14",
          confidence: ok ? "high" : "low",
          reason: ok
            ? "14 digits and the GS1 mod-10 check digit matches — valid GTIN-14 / ITF-14 carton code."
            : "14 digits but the GS1 mod-10 check digit doesn't match.",
        }),
      );
    }

    // ISBN-10 (rare — 10 all-digit)
    if (len === 10) {
      const ok = validIsbn10(cleaned);
      candidates.push(
        withTool({
          format: "ISBN-10",
          confidence: ok ? "high" : "low",
          reason: ok
            ? "10 digits and the mod-11 ISBN-10 check digit matches."
            : "10 digits but the ISBN-10 mod-11 check digit doesn't match.",
        }),
      );
    }
  }

  // ISBN-10 ending in X
  if (cleaned.length === 10 && /^\d{9}[Xx]$/.test(cleaned)) {
    const ok = validIsbn10(cleaned);
    candidates.push(
      withTool({
        format: "ISBN-10",
        confidence: ok ? "high" : "low",
        reason: ok
          ? "9 digits plus an X check character — valid ISBN-10."
          : "Looks like an ISBN-10 (ends with X) but the mod-11 check digit doesn't match.",
      }),
    );
  }

  // Fallbacks
  if (candidates.length === 0) {
    const looksUrl = /^https?:\/\//i.test(cleaned) || /^[a-z]+:[^\s]/i.test(input);
    if (looksUrl || !isAllDigits) {
      candidates.push(
        withTool({
          format: "QR / Text",
          confidence: "medium",
          reason: looksUrl
            ? "Contains a URL scheme — typical QR Code payload."
            : "Contains non-digit characters — most likely a QR Code or Data Matrix payload (free text).",
        }),
      );
    } else {
      candidates.push(
        withTool({
          format: "Unknown",
          confidence: "low",
          reason: `No standard barcode format matches a ${len}-digit numeric value. Common lengths are 8, 12, 13 and 14.`,
        }),
      );
    }
  }

  // Order by confidence (high → low) then by listing order.
  const rank: Record<Confidence, number> = { high: 0, medium: 1, low: 2 };
  candidates.sort((a, b) => rank[a.confidence] - rank[b.confidence]);

  return { state: "ok", cleaned, candidates };
}
